import { useEffect } from "react";

/**
 * Animates any element with a [data-count] attribute from 0 to its
 * real value once it scrolls into view. Originally only wired up on
 * the Home page's Impact section — extracted here so every page's
 * real stat numbers (183M+, $100K, 2x, etc.) get the same treatment
 * instead of sitting as static text while Home's numbers feel alive.
 *
 * data-count: the target number (required)
 * data-prefix / data-suffix: any surrounding text that isn't part of
 * the count itself (e.g. prefix="$" suffix="K" for "$100K")
 */
export function useCountUp() {
  useEffect(() => {
    function countUp(el) {
      const target = parseFloat(el.getAttribute("data-count"));
      const prefix = el.getAttribute("data-prefix") || "";
      const suffix = el.getAttribute("data-suffix") || "";
      const isInt = Number.isInteger(target);
      const dur = 1400;
      const start = performance.now();
      function tick(now) {
        const p = Math.min((now - start) / dur, 1);
        const eased = 1 - Math.pow(1 - p, 3);
        const val = target * eased;
        el.textContent =
          prefix + (isInt ? Math.round(val).toLocaleString() : val.toFixed(1)) + suffix;
        if (p < 1) requestAnimationFrame(tick);
      }
      requestAnimationFrame(tick);
    }
    const countEls = document.querySelectorAll("[data-count]");
    const countIo = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            countUp(e.target);
            countIo.unobserve(e.target);
          }
        });
      },
      { threshold: 0.4 }
    );
    countEls.forEach((el) => countIo.observe(el));
    return () => countIo.disconnect();
  }, []);
}